import { addMinutes, startOfMinute } from "date-fns";
import { format } from "fecha";

import { distinct } from "../utils/arrayutil/distinct";
import { between } from "../utils/between";
import { InheritableEventEmitter } from "../utils/inheritableeventemitter";
import { getStringValFromElem } from "../utils/jqutil";
import { pickElement, pickElements } from "../utils/pickelements";

/**
 * 震源検索→検索条件エリア→情報パネル基底クラス
 */
export abstract class AbstractPanel<T>
    extends InheritableEventEmitter<{ update: [params: Partial<T>] }> {

    /** パネルのルート要素 */
    #elem: HTMLElement;

    /**
     * コンストラクター。
     */
    constructor(elem: HTMLElement) {
        super();

        this.#elem = elem;
    }

    /**
     * このパネルのルート要素を取得する。
     *
     * @returns パネルのルート要素
     */
    getElem() {
        return this.#elem;
    }

    /**
     * このパネルを表示する。
     */
    show() {
        this.#elem.classList.remove("hidden");
    }

    /**
     * このパネルを非表示にする。
     */
    hide() {
        this.#elem.classList.add("hidden");
    }

    /**
     * パネル内の最初の入力要素でchangeイベントを発火させる。
     */
    trigger() {
        const elems = pickElements("input, select", this.#elem);
        if (elems.length) {
            elems[0].dispatchEvent(new Event("change", { bubbles: true }));
        }
    }

    /**
     * 指定されたセレクターに一致する要素から文字列値を取得する。
     *
     * @param query セレクター文字列
     * @returns 取得した文字列値（undefinedの場合もあり）
     */
    protected getString(query: string) {
        const elem = pickElement(query, this.#elem);
        return getStringValFromElem(elem);
    }

    /**
     * 指定されたセレクターに一致する要素から数値を取得する。
     *
     * @param query セレクター文字列
     * @returns 取得した数値（undefinedの場合もあり）
     */
    protected getNumber(query: string) {
        const val = this.getString(query);
        if (val) {
            const parsed = Number.parseFloat(val);
            return (Number.isNaN(parsed)) ? undefined : parsed;
        } else {
            return undefined;
        }
    }

    /**
     * 指定されたセレクターに一致する要素から日時を取得し、フォーマットする。
     *
     * @param query セレクター文字列
     * @param mode 丸めモード（"floor"は切り捨て、"ceil"は切り上げ）
     * @returns フォーマットされた日時文字列（undefinedの場合もあり）
     */
    protected getDate(query: string, mode: "floor" | "ceil") {
        const val = this.getString(query);
        if (!val) {
            return undefined;
        }

        const dt = new Date(val);
        const adjusted = startOfMinute((mode === "floor") ? dt : addMinutes(dt, 1));

        // `window.isNaN()`は無効な日付判定に使えるが
        // `Number.isNaN()`は使えないというげっそりする仕様に注意

        // oxlint-disable-next-line prefer-number-properties
        return (isNaN(adjusted as unknown as number)) ?
            undefined :
            format(adjusted, "YYYY-MM-DDTHH:mm:ss");
    }

    /**
     * 指定されたセレクターに一致する要素から経度を取得する。
     * 値が-180から180の範囲外の場合はundefinedを返す。
     *
     * @param query セレクター文字列
     * @returns 経度の数値（範囲外またはundefinedの場合もあり）
     */
    protected getLon(query: string) {
        const val = this.getNumber(query);

        return (typeof val === "number" && between(-180, val, 180)) ? val : undefined;
    }

    /**
     * 指定されたセレクターに一致する要素から緯度を取得する。
     * 値が-90から90の範囲外の場合はundefinedを返す。
     *
     * @param query セレクター文字列
     * @returns 緯度の数値（範囲外またはundefinedの場合もあり）
     */
    protected getLat(query: string) {
        const val = this.getNumber(query);

        return (typeof val === "number" && between(-90, val, 90)) ? val : undefined;
    }

    /**
     * 指定されたセレクターに一致するチェックボックス等から選択された値を取得し、変換する。
     *
     * @param query セレクター文字列
     * @param converter 文字列を指定の型に変換する関数
     * @returns 変換された値の配列（undefinedの場合もあり）
     */
    protected getChecked<U>(query: string, converter: (s: string) => U) {
        try {
            const elems = pickElements(query, this.#elem);

            const val = elems
                .map((x) => getStringValFromElem(x))
                .join(",")
                .split(",")
                .map((x) => x.trim())
                .filter((x) => x !== "");

            if (val?.length) {
                return distinct(val.map((x) => converter(x)));
            } else {
                return undefined;
            }
        } catch {
            return undefined;
        }
    }
}
